import React from "react";
import { fireEvent, render, screen, waitFor } from "@testing-library/react";
import "@testing-library/jest-dom";
import ConfigPanel from "./ConfigPanel";

let mockTranscriptsEnabled = true;
let mockDocumentType: any;
const mockListTranscripts = jest.fn();
const mockGetDocument = jest.fn();

jest.mock("react-router-dom", () => ({
  ...jest.requireActual("react-router-dom"),
  useNavigate: () => jest.fn(),
}));

jest.mock("../../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({ workspace: { id: "workspace-1" } }),
}));

jest.mock("../../../../providers/FeatureFlagProvider", () => ({
  useFeatureFlag: () => mockTranscriptsEnabled,
}));

jest.mock("../../../../utils/useAuthFetch", () => ({
  useAuthFetch: () => jest.fn().mockResolvedValue({}),
}));

jest.mock("../../hooks/useTranscripts", () => ({
  useTranscripts: () => ({ listTranscripts: mockListTranscripts }),
}));

jest.mock("../../hooks/useDocumentTypes", () => ({
  useFetchDocumentTypeForDocument: () => ({
    documentType: mockDocumentType,
    error: null,
    loading: false,
  }),
}));

jest.mock("../../../../components/Loading", () => () => <div>Loading</div>);
jest.mock("../common/FileUploader", () => () => <div>File uploader</div>);
jest.mock("../common/DateInput", () => () => <div>Date input</div>);

const transcriptSlot = {
  SourceFieldID: 17,
  Name: "interview_transcript",
  Label: "Interview transcript",
  DocumentType: "Transcript",
  IsRequired: true,
  OrderIndex: 0,
};

const renderPanel = () =>
  render(
    <ConfigPanel
      documentTypeId={4}
      documentTypeVersion={1}
      documentId={9}
      documentStatus="Started"
      isGenerationActive={false}
      isGenerating={false}
      isRegeneratingSection={false}
      generationState={{
        currentSection: null,
        pendingSections: [],
        completedSections: [],
        error: null,
        isComplete: false,
        isGenerating: false,
        isStreamComplete: true,
        isWaitingForNext: false,
        exportState: "idle",
      }}
      onGenerate={jest.fn()}
      updateDocument={jest.fn().mockResolvedValue({})}
      deleteSourceDocument={jest.fn().mockResolvedValue(undefined)}
      createDocument={jest.fn().mockResolvedValue({})}
      getDocument={mockGetDocument}
    />,
  );

describe("Transcript source picker feature flag", () => {
  beforeEach(() => {
    mockTranscriptsEnabled = true;
    mockDocumentType = {
      Name: "Test document",
      document_type_inputs: [],
      document_type_sources: [transcriptSlot],
    };
    mockListTranscripts.mockReset().mockResolvedValue([
      {
        transcript_id: 12,
        title: "Leadership interview",
        status: "Completed",
        visibility: "Workspace",
        language: "en-US",
        folder_id: 2,
        folder_name: "Interviews",
        transcript_text: null,
        created_by_user_id: "user-1",
        created_date: "2026-10-18T00:00:00Z",
        modified_date: "2026-10-18T00:00:00Z",
        segments: [],
      },
    ]);
    mockGetDocument.mockReset().mockResolvedValue({
      DocumentID: 9,
      Title: "Draft",
      Status: "Started",
      DocumentType: { id: 4, name: "Test", version: 1, latestVersion: 1 },
      CreatedDate: "2026-10-19T00:00:00Z",
      ModifiedDate: null,
      sourceDocuments: [],
    });
  });

  it("lists completed transcripts in the source picker while enabled", async () => {
    renderPanel();

    const picker = await screen.findByPlaceholderText(
      "Search completed transcripts",
    );
    expect(picker).toBeInTheDocument();
    await waitFor(() =>
      expect(mockListTranscripts).toHaveBeenCalledWith(
        "workspace-1",
        undefined,
        "Completed",
        "",
      ),
    );
    fireEvent.click(screen.getByRole("button", { name: "Open" }));
    expect(await screen.findByText("Leadership interview")).toBeInTheDocument();
    expect(screen.getByText(/Interviews/)).toBeInTheDocument();
  });

  it("shows an unavailable state and no picker while disabled", async () => {
    mockTranscriptsEnabled = false;
    renderPanel();

    expect(
      await screen.findByText(
        /unavailable because Meridian transcripts are disabled/i,
      ),
    ).toBeInTheDocument();
    expect(
      screen.queryByPlaceholderText("Search completed transcripts"),
    ).not.toBeInTheDocument();
    expect(screen.queryByText("File uploader")).not.toBeInTheDocument();
    expect(mockListTranscripts).not.toHaveBeenCalled();
  });

  it("shows a load error with retry instead of an empty result", async () => {
    mockListTranscripts
      .mockRejectedValueOnce(new Error("network failure"))
      .mockResolvedValueOnce([
        {
          transcript_id: 12,
          title: "Leadership interview",
          status: "Completed",
          visibility: "Workspace",
          language: "en-US",
          folder_id: 2,
          folder_name: "Interviews",
          transcript_text: null,
          created_by_user_id: "user-1",
          created_date: "2026-10-18T00:00:00Z",
          modified_date: "2026-10-18T00:00:00Z",
          segments: [],
        },
      ]);
    renderPanel();

    expect(
      await screen.findByText("Could not load transcripts."),
    ).toBeInTheDocument();
    expect(
      screen.queryByText(
        "No completed transcripts are available in this workspace.",
      ),
    ).not.toBeInTheDocument();

    fireEvent.click(screen.getByRole("button", { name: "Retry" }));
    await waitFor(() => expect(mockListTranscripts).toHaveBeenCalledTimes(2));
    fireEvent.click(screen.getByRole("button", { name: "Open" }));
    expect(await screen.findByText("Leadership interview")).toBeInTheDocument();
  });

  it("keeps a saved transcript option when it is outside the first page", async () => {
    mockGetDocument.mockResolvedValue({
      DocumentID: 9,
      Title: "Draft",
      Status: "Started",
      DocumentType: { id: 4, name: "Test", version: 1, latestVersion: 1 },
      CreatedDate: "2026-10-19T00:00:00Z",
      ModifiedDate: null,
      sourceDocuments: [
        {
          transcriptId: 999,
          sourceFieldId: 17,
          originalName: "Saved transcript beyond first 100",
          uploadDate: "2026-10-17T00:00:00Z",
        },
      ],
    });
    renderPanel();

    const picker = (await screen.findByRole("combobox")) as HTMLInputElement;
    await waitFor(() =>
      expect(picker.value).toContain("Saved transcript beyond first 100"),
    );
    expect(mockGetDocument).toHaveBeenCalledWith(9, "workspace-1");
  });
});
